import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useAuthContext from '../../hooks/useAuthContext';
import {
  Chip,
  Drawer,
  Icon,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  Avatar,
  ListItemButton,
  ListItemIcon,
  ListSubheader,
  ListItemText,
  Tooltip,
  MenuItem,
  Menu,
  useMediaQuery
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { NotificationsPanel, ChangeBusinessUnitPanel, ChangePasswordModal } from '../../components';
import { useTranslation } from 'react-i18next';
import { faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import UserRole from '../../constants/user-role';
import useFeatures, { FeatureType } from '../../hooks/features';
import { brand } from '../../theme/brand';
import { tokens } from '../../theme/tokens';
import { V4_SMALL_SCREEN } from '../../theme/v4Compat';

const drawerWidth = 256;

const Header: React.FC = () => {
  const { state } = useAuthContext();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const isSmall = useMediaQuery(V4_SMALL_SCREEN);
  const { hasFeature } = useFeatures();

  const isVet = state.role === UserRole.veterinary;
  const [open, setOpen] = React.useState(false);
  const [openedManually, setOpenedManually] = React.useState(false);

  const triggerResize = () => {
    window.dispatchEvent(new CustomEvent('resize'));
  };

  const handleDrawerManually = () => {
    setOpenedManually(!openedManually);
    setOpen(!open);
  };

  const handleDrawerOpen = () => {
    setOpen(true);
    triggerResize();
  };

  const handleDrawerClose = () => {
    if (!openedManually) {
      setOpen(false);
      triggerResize();
    }
  };

  useEffect(() => {
    setOpen(false);
  }, [isSmall]);

  const isSelectedOn = (path: string): boolean => location.pathname.startsWith(path);

  const menuItem = (link: string, path: string, tKey: string, icon: string, disabled = false, beta = false) => {
    const selected = isSelectedOn(path);
    const color = selected ? 'primary' : 'inherit';

    return (
      <ListItemButton
        selected={selected}
        disabled={disabled}
        component={Link}
        to={link}
        // v9 tints a selected row with the primary colour; the drawer keeps the neutral grey it always had.
        sx={{ pl: 3, '&.Mui-selected, &.Mui-selected:hover': { bgcolor: 'action.selected' } }}
      >
        <ListItemIcon>
          <Icon color={color}>{icon}</Icon>
        </ListItemIcon>
        <ListItemText
          primary={
            beta ? (
              <span className="flex items-center gap-2">
                {t(tKey)}
                <Chip size="small" label={t('f-beta')} className="!bg-purple-100 !text-purple-800 !h-5 !text-xs" />
              </span>
            ) : (
              t(tKey)
            )
          }
        />
      </ListItemButton>
    );
  };

  const renderManagerMenuItems = () => {
    return (
      <List disablePadding>
        <ListSubheader inset>{t('f-manager')}</ListSubheader>

        {menuItem('/farm', '/farm/', 'f-farm-info', 'home_work')}
        {menuItem(
          '/emission-source-usage',
          '/emission-source-usage/',
          'f-emission-source-usage-info',
          'energy_savings_leaf'
        )}

        {hasFeature(FeatureType.USE_SLAUGHTER_PREPARATION_ANIMALS)
          ? menuItem(
              '/slaughter-declarations',
              '/slaughter-declarations/',
              'f-slaughter-declarations',
              'pending_actions'
            )
          : null}
        {menuItem('/farm-settings', '/farm-settings/', 'f-settings-farm', 'engineering')}
      </List>
    );
  };

  const renderAdminMenuItems = () => {
    return (
      <List disablePadding>
        <ListSubheader inset>{t('f-admin')}</ListSubheader>

        {/* No farms-overview entry: it is one of the two views behind the Dashboard item above, and a second
            way in would leave the drawer highlighting neither of them. */}
        {menuItem('/settings', '/settings', 'f-settings-sys', 'admin_panel_settings')}
        {menuItem('/system', '/system', 'f-settings-sys-data', 'data_usage')}
        {menuItem('/devices', '/devices', 'f-devices', 'app_settings_alt')}
      </List>
    );
  };

  const renderUserMenuItems = () => {
    return (
      <List disablePadding>
        <ListSubheader inset>{t('f-user')}</ListSubheader>

        {menuItem('/dashboard', '/dashboard', 'f-dashboard', 'assessment')}
        {menuItem('/animal', '/animal', 'f-animals', 'pets')}
        {menuItem('/locations', '/locations', 'f-locations', 'room')}
        {menuItem('/medical-info', '/medical-info', 'f-com-medical-info', 'medical_services')}
        {menuItem('/strategy', '/strategy', 'f-strategy', 'trending_up')}
        {menuItem('/tasks', '/tasks', 'f-tasks', 'list')}
        {menuItem('/tasks-execution', '/tasks-execution', 'f-tasks-labor', 'assignment_add')}
      </List>
    );
  };

  const renderVeterinaryMenuItems = () => {
    return (
      <List disablePadding>
        <ListSubheader inset>{t('f-vets')}</ListSubheader>

        {menuItem('/dashboard', '/dashboard', 'f-dashboard', 'assessment')}
        {menuItem(`/veterinary/${state.vetId}`, '/veterinary', 'f-vets-view-title', 'home_work')}
        {menuItem('/vet/locations', '/vet/locations', 'f-locations', 'room')}
        {menuItem('/vet/orders', '/vet/orders', 'f-supp-order-history', 'shopping_cart')}
        {menuItem('/vet/medical-supplies', '/vet/medical-supplies', 'f-supplies', 'medical_services')}
        {menuItem('/vet/labels', '/vet/labels', 'f-labels', 'sticky_note_2')}
        {menuItem('/vet/bottles', '/vet/bottles', 'f-bottles', 'medication')}
        {menuItem('/vet/vaccination-rules', '/vet/vaccination-rules', 'f-vaccination-rules', 'rule')}

        {hasFeature(FeatureType.USE_MEDICATION_CATALOGUE)
          ? menuItem('/vet/medications', '/vet/medications', 'f-medications-title', 'inventory_2', false, true)
          : null}
      </List>
    );
  };

  const handleLogOut = () => {
    localStorage.clear();
    window.location.href = '/login';
  };

  const [userAccountAnchorEl, setUserAccountAnchorEl] = React.useState<null | HTMLElement>(null);
  const [changePasswordDialogOpen, setChangePasswordDialogOpen] = React.useState(false);

  const handleUserAccountClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setUserAccountAnchorEl(event.currentTarget);
  };

  const handleUserAccountClose = () => {
    setUserAccountAnchorEl(null);
  };

  const viewCurrentUser = () => {
    navigate(`/user/${state.userId}`);
    handleUserAccountClose();
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const currentUnit = state.businessUnits?.find((u) => u.id === state.selectedBusinessUnitId);

  const renderUserMenu = () => {
    if (state.role === 'veterinary') return renderVetUserMenu();

    const farmName = currentUnit?.name ?? state.farmName;
    // Held in a local so the `farmNumber && ...` guard below still narrows inside the onClick
    // closure - a vet token has no farmNumber, and the block is simply not rendered then.
    const farmNumber = state.farmNumber;

    return (
      <div>
        <MenuItem
          disableRipple
          style={{ flexDirection: 'column', alignItems: 'flex-start', cursor: 'default', userSelect: 'text' }}
        >
          <Typography variant="body1" style={{ fontWeight: 600, userSelect: 'text' }}>
            {state.userName}
          </Typography>
          {farmName && (
            <Typography variant="body2" color="textSecondary" style={{ userSelect: 'text' }}>
              {farmName}
            </Typography>
          )}
          {farmNumber && (
            <Tooltip title={t('f-copy')} placement="right">
              <Typography
                variant="caption"
                color="textSecondary"
                style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                onClick={() => copyToClipboard(farmNumber)}
              >
                {farmNumber}
              </Typography>
            </Tooltip>
          )}
        </MenuItem>
        <Divider />

        <MenuItem onClick={viewCurrentUser}>
          <ListItemIcon>
            <Icon>{'account_box'}</Icon>
          </ListItemIcon>
          <ListItemText primary={t('f-account-profile')} />
        </MenuItem>

        <MenuItem onClick={handleLogOut}>
          <ListItemIcon>
            <Icon>{'exit_to_app'}</Icon>
          </ListItemIcon>
          <ListItemText primary={t('f-logout')} />
        </MenuItem>
      </div>
    );
  };

  //todo: adjust menu items when new feature unlocks
  const renderVetUserMenu = () => {
    // Same reason as farmNumber above: a local keeps the guard's narrowing inside the onClick.
    const vetNumber = state.vetNumber;

    return (
      <div>
        <MenuItem
          disableRipple
          style={{ flexDirection: 'column', alignItems: 'flex-start', cursor: 'default', userSelect: 'text' }}
        >
          <Typography variant="body1" style={{ fontWeight: 600, userSelect: 'text' }}>
            {state.userName}
          </Typography>
          {currentUnit?.name && (
            <Typography variant="body2" color="textSecondary" style={{ userSelect: 'text' }}>
              {currentUnit.name}
            </Typography>
          )}
          {vetNumber && (
            <Tooltip title={t('f-copy')} placement="right">
              <Typography
                variant="caption"
                color="textSecondary"
                style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                onClick={() => copyToClipboard(vetNumber)}
              >
                {vetNumber}
              </Typography>
            </Tooltip>
          )}
        </MenuItem>
        <Divider />

        <MenuItem
          onClick={() => {
            setChangePasswordDialogOpen(true);
            handleUserAccountClose();
          }}
        >
          <ListItemIcon>
            <Icon>{'lock'}</Icon>
          </ListItemIcon>
          <ListItemText primary={t('f-account-change-password')} />
        </MenuItem>

        <MenuItem onClick={handleLogOut}>
          <ListItemIcon>
            <Icon>{'exit_to_app'}</Icon>
          </ListItemIcon>
          <ListItemText primary={t('f-logout')} />
        </MenuItem>
      </div>
    );
  };

  const renderUserAccountMenu = () => {
    return (
      <div>
        <IconButton id="account-badge" color="inherit" onClick={handleUserAccountClick}>
          <Avatar alt="User Logo">
            <FontAwesomeIcon icon={faUser} />
          </Avatar>
        </IconButton>

        <Menu
          id="account-menu"
          anchorEl={userAccountAnchorEl}
          open={Boolean(userAccountAnchorEl)}
          onClose={handleUserAccountClose}
        >
          {renderUserMenu()}
        </Menu>
      </div>
    );
  };
  //#endregion user account header menu

  /** The shell's width and margin transitions: quick to open (enteringScreen), slower to close. */
  const slide = (theme: Theme, properties: string | string[]) =>
    theme.transitions.create(properties, {
      easing: theme.transitions.easing.sharp,
      duration: open ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen
    });

  return (
    <header>
      <AppBar
        color="primary"
        position="fixed"
        sx={(theme) => ({ zIndex: theme.zIndex.drawer + 1, transition: slide(theme, ['width', 'margin']) })}
      >
        <Toolbar sx={{ pr: 3 }}>
          <IconButton edge="start" color="inherit" onClick={handleDrawerManually} sx={{ mr: 4.5 }}>
            <Icon>{'menu'}</Icon>
          </IconButton>

          <IconButton color="inherit">
            <Avatar alt={brand.logoAlt} src={brand.logoSrc} />
          </IconButton>

          <Typography component="h1" variant="h6" noWrap sx={{ flexGrow: 1, pl: 1 }}>
            {brand.appName}
          </Typography>

          <ChangeBusinessUnitPanel />

          {!isVet && <NotificationsPanel />}

          {renderUserAccountMenu()}
        </Toolbar>
      </AppBar>

      <Drawer
        variant={!isSmall ? 'permanent' : 'temporary'}
        open={open}
        onMouseEnter={!isSmall ? handleDrawerOpen : undefined}
        onMouseLeave={!isSmall ? handleDrawerClose : undefined}
        onClose={(_event, reason) => {
          // A backdrop click on the small-screen overlay also ends a manual opening, as it always has.
          if (reason === 'backdropClick') handleDrawerManually();
          handleDrawerClose();
        }}
        slotProps={{
          paper: {
            sx: (theme) => ({
              position: 'relative',
              whiteSpace: 'nowrap',
              // Closed, the permanent drawer is an icon rail; the small-screen overlay has no rail.
              width: open ? drawerWidth : isSmall ? 0 : theme.spacing(9),
              overflowX: open ? undefined : 'hidden',
              height: '100vh',
              overflowY: 'auto',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
              background: tokens.sidebar,
              transition: slide(theme, 'width')
            })
          }
        }}
      >
        <Toolbar disableGutters sx={{ justifyContent: 'flex-end', px: 1 }}>
          <Tooltip arrow title={t('f-menu-close')} placement="left">
            <IconButton
              onClick={() => {
                setOpenedManually(false);
                setOpen(false);
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
        <Divider />

        {state.role === UserRole.user && (
          <>
            {renderUserMenuItems()}
            <Divider />
          </>
        )}

        {(state.role === UserRole.manager || state.role === UserRole.admin) && (
          <>
            {renderUserMenuItems()}
            {renderManagerMenuItems()}
            <Divider />
          </>
        )}

        {state.role === UserRole.admin && (
          <>
            {renderAdminMenuItems()}
            <Divider />
          </>
        )}

        {state.role === 'veterinary' && (
          <>
            {renderVeterinaryMenuItems()}
            <Divider />
          </>
        )}
      </Drawer>

      <ChangePasswordModal opened={changePasswordDialogOpen} onClose={() => setChangePasswordDialogOpen(false)} />
    </header>
  );
};

export default Header;
